import React, { useEffect, useState } from 'react';
import {
  DNABox,
  DNAButton,
  DNAButtonProps,
  DNAText,
  DNAContextMenu,
  Rotate,
  Icon,
  DNAButtonGroup,
} from '@alucio/lux-ui';
import { StyleProp, ViewStyle, StyleSheet } from 'react-native';
import VersioningPanel from 'src/screens/Publishers/Versioning/VersioningPanel/VersioningPanel';
import { generateClient } from 'aws-amplify/api';
import { createWebDocument } from '@alucio/aws-beacon-amplify/src/graphql/mutations'
import { useDocumentVersionORM } from 'src/state/redux/selector/document';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';

type ConditionalWrapDnaGroupButtonsProps = {
  children: React.ReactNode;
  isDisabled?: boolean;
}

const ConditionalWrapDnaGroupButtons = ({ children, isDisabled }: ConditionalWrapDnaGroupButtonsProps) => {
  return isDisabled ? <>{children}</> : <DNAButtonGroup>{children}</DNAButtonGroup>
}

/**
 * Custom hook to handle website document creation and management
 */
export const useWebsiteDocumentCreation = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [documentVersionId, setDocumentVersionId] = useState<string>('');
  const [documentId, setDocumentId] = useState<string>();

  const documentVersionORM = useDocumentVersionORM(documentVersionId);

  useEffect(() => {
    if (documentVersionORM && documentVersionORM.model.documentId !== documentId) {
      setDocumentId(documentVersionORM.model.documentId);
      setDocumentVersionId('');
    }
  }, [documentVersionORM, documentId]);

  const createWebsiteDocument = async () => {
    try {
      setIsCreating(true);
      const appsyncClient = generateClient();
      const { data } = await appsyncClient.graphql({
        query: createWebDocument,
      });
      setDocumentVersionId(data.createWebDocument.id);
    } catch (e) {
      console.warn('Error creating website document.', e);
    } finally {
      setIsCreating(false);
    }
  };

  return {
    isCreating,
    documentVersionId,
    documentId,
    setDocumentId,
    createWebsiteDocument,
  };
};

interface Props extends DNAButtonProps {
  disableButtonGroup?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  context?: 'round';
  iconStyle?: StyleProp<ViewStyle>;
  onSelect: (files: File[]) => void;
  text: string;
  acceptedTypes?: string;
  showContextMenu?: boolean;
}

const styles = StyleSheet.create({
  processingIcon: {
    height: 16,
    width: 16,
    color: colors['color-text-white'],
  },
})

function SelectFileButton(props: Props) {
  const enableZipDocuments = useFeatureFlags('enableZipDocuments');
  const acceptedFileTypes = enableZipDocuments
    ? '.pptx,.pdf,.mp4,.mov,.avi,.mpeg,.webm,.mkv,.zip'
    : '.pptx,.pdf,.mp4,.mov,.avi,.mpeg,.webm,.mkv';

  const {
    acceptedTypes = acceptedFileTypes,
    multiple,
    onSelect,
    text,
    disabled,
    showContextMenu,
    disableButtonGroup,
    ...restButtonProps
  } = props;

  // Use the custom hook for website document creation and management
  const {
    isCreating,
    documentId,
    setDocumentId,
    createWebsiteDocument,
  } = useWebsiteDocumentCreation();

  function onFilesSelected(event): void {
    event.stopPropagation();
    event.preventDefault();
    onSelect([...event.target.files]);
    // Set value to empty string so onChange always fire
    setValue('');
  }

  const [value, setValue] = useState('');

  const uploadBtnLoadingChildren: React.FC = () => {
    return (
      <>
        <Rotate>
          <Icon style={styles.processingIcon} name="refresh" />
        </Rotate>
        <DNAText colorMode="dark" style={{ marginLeft: 8 }}>Creating...</DNAText>
      </>
    )
  }

  return (
    <DNABox testID="upload-button">
      <input
        accept={acceptedTypes}
        id="uploadButton"
        type="file"
        style={{ display: 'none' }}
        multiple={!!multiple}
        value={value}
        onChange={onFilesSelected}
        disabled={disabled}
      />
      <ConditionalWrapDnaGroupButtons isDisabled={disableButtonGroup}>
        <label htmlFor="uploadButton">
          <DNAButton
            {...restButtonProps}
            disabled={isCreating}
            children={isCreating ? uploadBtnLoadingChildren : text}
          />
        </label>
        { showContextMenu && (
          <DNAContextMenu>
            <DNAContextMenu.Anchor>
              <DNAButton
                onPress={() => { }}
                iconLeft="caret-down"
                disabled={isCreating}
              />
            </DNAContextMenu.Anchor>
            <DNAContextMenu.Items>
              <DNAContextMenu.Item
                collapseOnPress
                delay={100} // Workaround because sometimes the item deletes too fast before the ContextMenu can hide itself
                title="Create website document"
                onPress={createWebsiteDocument}
              />
            </DNAContextMenu.Items>
          </DNAContextMenu>
        )}
      </ConditionalWrapDnaGroupButtons>
      <VersioningPanel
        documentId={documentId}
        setDocumentId={setDocumentId}
      />
    </DNABox>
  );
}

export default SelectFileButton;
